import { Schema } from "effect";
import { ChannelThreadAnalyticsHandoffSource } from "./analytics";

// Email thread channel data (gmail, outlook integrations)
export const EmailThreadChannelData = Schema.Struct({
  subject: Schema.NullOr(Schema.String),
  fromEmail: Schema.optionalWith(Schema.NullOr(Schema.String), {
    default: () => null,
  }),
});
export type EmailThreadChannelData = typeof EmailThreadChannelData.Type;

// Meta comment thread channel data (facebook, instagram integrations)
export const MetaCommentThreadChannelData = Schema.Struct({
  previewUrl: Schema.NullOr(Schema.String),
  permalink: Schema.NullOr(Schema.String),
  topLevelCommentId: Schema.NullOr(Schema.String),
});
export type MetaCommentThreadChannelData =
  typeof MetaCommentThreadChannelData.Type;

export const CHAT_FLOW_INPUT_STEP_TYPES = [
  "default",
  "collect_email",
  "code_verification",
  "offering_convert_to_email",
] as const;
export const ChatFlowInputStepType = Schema.Literal(
  ...CHAT_FLOW_INPUT_STEP_TYPES,
);
export type ChatFlowInputStepType = typeof ChatFlowInputStepType.Type;

export const ChatFlowThreadChannelData = Schema.Struct({
  lastKnownStepId: Schema.NullOr(Schema.Number),
  inputStepType: Schema.NullOr(ChatFlowInputStepType),
  fourDigitCodeId: Schema.NullOr(Schema.String),
});
export type ChatFlowThreadChannelData = typeof ChatFlowThreadChannelData.Type;

// SMS thread channel data (telnyx_sms integration)
export const SmsThreadChannelData = Schema.Struct({
  toNumber: Schema.String,
  fromNumber: Schema.String,
});
export type SmsThreadChannelData = typeof SmsThreadChannelData.Type;

export const MobileMessageThreadChannelData = Schema.Struct({
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
});
export type MobileMessageThreadChannelData =
  typeof MobileMessageThreadChannelData.Type;

/**
 * Voice thread channel data — pinned to the (customer, merchant)
 * phone-number pair that owns the thread. Every voice call between
 * the same pair lands on this thread as another message; new pairs
 * spawn new threads.
 */
export const VoiceThreadChannelData = Schema.Struct({
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
});
export type VoiceThreadChannelData = typeof VoiceThreadChannelData.Type;

// Union of all thread channel data types (use integrationType to distinguish)
// Integration type -> channel data mapping:
//   gmail, outlook -> EmailThreadChannelData
//   facebook, instagram -> MetaCommentThreadChannelData
//   redoChatFlow -> ChatFlowThreadChannelData
//   telnyx_sms -> SmsThreadChannelData
//   mobileMessage -> MobileMessageThreadChannelData
//   telnyx_voice -> VoiceThreadChannelData
//   tiktok, attentive, postscript, redoChatAi, redoChatLive -> null
export const ThreadChannelData = Schema.Union(
  EmailThreadChannelData,
  MetaCommentThreadChannelData,
  ChatFlowThreadChannelData,
  SmsThreadChannelData,
  MobileMessageThreadChannelData,
  VoiceThreadChannelData,
);
export type ThreadChannelData = typeof ThreadChannelData.Type;

// Thread analytics data (not channel-specific, any thread can have this)
export const ThreadAnalyticsData = Schema.Struct({
  handoffCategory: Schema.NullOr(Schema.String),
  handoffReason: Schema.NullOr(Schema.String),
  handoffSuggestedCategory: Schema.NullOr(Schema.String),
  handoffSource: Schema.NullOr(ChannelThreadAnalyticsHandoffSource),
  handoffSuggestedToolName: Schema.NullOr(Schema.String),
  handoffSuggestedToolEnabled: Schema.NullOr(Schema.Boolean),
});
export type ThreadAnalyticsData = typeof ThreadAnalyticsData.Type;
